import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import AbiShell from "@/pages/abi/AbiShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, Trash2, KeyRound, Power, Loader2, Users, ExternalLink, Copy } from "lucide-react";

export default function AbiTerzi() {
  const [ok, setOk] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", label: "", quota_mese: "" });
  const [busy, setBusy] = useState(false);
  const portalUrl = `${window.location.origin}/bi-portal`;

  const load = () => {
    api.get("/abi/external/config").then(() => {
      setOk(true);
      api.get("/abi/external/users").then(({ data }) => setUsers(data)).catch(() => {});
    }).catch(() => setOk(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!form.username.trim() || form.password.length < 6) {
      toast.error("Username e password (min 6 caratteri) obbligatori"); return;
    }
    setBusy(true);
    try {
      await api.post("/abi/external/users", {
        username: form.username.trim(), password: form.password,
        label: form.label.trim(), quota_mese: form.quota_mese,
      });
      toast.success(`Utenza "${form.username.trim().toLowerCase()}" creata`);
      setForm({ username: "", password: "", label: "", quota_mese: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = async (u) => {
    try { await api.put(`/abi/external/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`Nuova password per "${u.username}" (min 6 caratteri):`);
    if (!pw) return;
    try { await api.put(`/abi/external/users/${u.id}`, { password: pw }); toast.success("Password aggiornata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Eliminare l'utenza "${u.username}"?`)) return;
    try { await api.delete(`/abi/external/users/${u.id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (ok === false) {
    return <AbiShell><div className="p-10 text-center text-slate-500" data-testid="abi-terzi-denied">
      Accesso riservato all'amministratore della card Business Intelligence.</div></AbiShell>;
  }

  return (
    <AbiShell>
      <div className="max-w-5xl mx-auto" data-testid="abi-terzi">
        <div className="flex items-center gap-2 mb-1"><Users className="h-5 w-5 text-violet-600" />
          <h1 className="font-heading text-xl font-semibold text-slate-900">Accessi Terzi — Portale Business Intelligence</h1></div>
        <p className="text-sm text-slate-500 mb-4">Crea le utenze per soggetti esterni. Accedono al portale dedicato
          senza essere registrati sulla Suite. Link portale:{" "}
          <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">{portalUrl} <ExternalLink className="h-3 w-3" /></a>
          <button className="ml-2 text-slate-400 hover:text-slate-600" onClick={() => { navigator.clipboard?.writeText(portalUrl); toast.success("Link copiato"); }} data-testid="abi-terzi-copy-portal"><Copy className="h-3.5 w-3.5" /></button>
        </p>

        <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end" data-testid="abi-terzi-form">
          <div><label className="text-xs text-slate-500 block mb-1">Username</label>
            <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="studio.rossi" data-testid="abi-terzi-username" /></div>
          <div><label className="text-xs text-slate-500 block mb-1">Password</label>
            <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 caratteri" data-testid="abi-terzi-password" /></div>
          <div><label className="text-xs text-slate-500 block mb-1">Etichetta</label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Studio Rossi" data-testid="abi-terzi-label" /></div>
          <div><label className="text-xs text-slate-500 block mb-1">Quota/mese</label>
            <Input value={form.quota_mese} onChange={(e) => setForm({ ...form, quota_mese: e.target.value })} placeholder="illimitata" data-testid="abi-terzi-quota" /></div>
          <Button onClick={create} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="abi-terzi-create">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1.5" />} Crea utenza</Button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <Table data-testid="abi-terzi-table">
            <TableHeader><TableRow>
              <TableHead>Username</TableHead><TableHead>Etichetta</TableHead><TableHead>Stato</TableHead>
              <TableHead>Quota</TableHead><TableHead>Usati (mese)</TableHead><TableHead>Ultimo accesso</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`abi-terzi-row-${u.username}`}>
                  <TableCell className="font-medium">{u.username}</TableCell>
                  <TableCell className="text-slate-600">{u.label || "—"}</TableCell>
                  <TableCell>{u.active ? <Badge className="bg-emerald-100 text-emerald-700">Attivo</Badge> : <Badge className="bg-slate-200 text-slate-600">Disattivo</Badge>}</TableCell>
                  <TableCell>{u.quota_mese ?? "∞"}</TableCell>
                  <TableCell>{u.usati_mese}</TableCell>
                  <TableCell className="text-xs text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleString("it-IT") : "mai"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Reset password" onClick={() => resetPw(u)} data-testid={`abi-terzi-reset-${u.username}`}><KeyRound className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title={u.active ? "Disattiva" : "Attiva"} onClick={() => toggle(u)} data-testid={`abi-terzi-toggle-${u.username}`}><Power className={`h-4 w-4 ${u.active ? "text-emerald-600" : "text-slate-400"}`} /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" title="Elimina" onClick={() => del(u)} data-testid={`abi-terzi-del-${u.username}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {!users.length && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nessuna utenza Terzi creata</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </AbiShell>
  );
}
